/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Versorgungsausgleich_700px.jpg";
import { HashLink as Link } from "react-router-hash-link";
import { gaSet } from "../../../services/cookieService";
import Typography from "@mui/material/Typography";
import { Paper, Link as MuiLink } from "@mui/material";
import guidebookContentStyle from "../guidebookContentStyle";
import MuiListNativeStyled from "../../List/MuiListNativeStyled";

const GuidebookDivorceChildren = () => {
  const title = "Ratgeber - Versorgungsausgleich | Legalbird";
  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta
          name="description"
          content="Wir erklären Ihnen alles, was Sie über den Versorgungsausgleich wissen müssen und
        wie Sie den Versorgungsausgleich ausschließen können."
        />
      </Helmet>

      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan
          slogan={"Einfach erklärt: Versorgungsausgleich"}
          backgroundImagePath={backgroundTeaser}
          height={300}
          headingColor={"#333333"}
        />
        <TextContainer>
          <p>
            Bei einer Scheidung werden Sie früher oder später über den Versorgungsausgleich “stolpern”. Der Versorgungsausgleich ist für Sie vermutlich ein
            neuer Begriff und er ist ein wichtiger Bestandteil im Scheidungsverfahren, daher erklären wir Ihnen diesen hier.
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Was ist der Versorgungsausgleich?
          </Typography>
          <p>
            Während einer Ehe zahlen oftmals beide Partner über Ihre Arbeit in die Rentenkasse zur Altersvorsorge ein. Wenn ein Partner während der Ehe mehr
            einzahlt (weil er mehr verdient) als der andere Partner, wird diese "Mehrversorgung" bei einer Scheidung durch das Gericht ausgeglichen - dies nennt
            man "Versorgungsausgleich". Bei einer Scheidung müssen die während der Ehe erworbenen Rentenansprüche zwischen den Ehepartnern aufgeteilt werden. Um
            die Berechnung des Versorgungsausgleichs zu erleichtern, sind beide Ehegatten dazu verpflichtet, dem Gericht Auskunft über während der Ehezeit
            erworbene Ansprüche zu erteilen. Sobald Sie Ihre Scheidung eingereicht haben, erhalten Sie daher vom Familiengericht ein Schreiben, in dem alle
            Informationen für den Versorgungsausgleich abgefragt werden. Erst wenn alle Auskünfte zu den Renten vorliegen und der Versorgungsausgleich berechnet
            wurde, kann das Familiengericht den Gerichtstermin für die Scheidung festlegen.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Für wen ist der Versogungsausgleich relevant?
          </Typography>
          <p>
            Im Normalfall wird der Versorgungsausgleich immer automatisch nach Beantragung der Scheidung durch das Familiengericht durchgeführt. Dem liegt der
            Gedanke zugrunde, dass derjenige Ehegatte, der während der Ehe z.B. den Haushalt geführt und die Kinder betreut hat, im Alter eine eigene soziale
            Absicherung haben soll. Wird der Versorgungsausgleich durchgeführt, ist mit einer Dauer von ca. 3 bis 6 Monaten zu rechnen. Erst wenn der
            Versorgungsausgleich berechnet ist, wird das Gericht den eigentlichen Scheidungstermin festlegen.
          </p>
          <p>
            Falls beidseitig gewünscht, kann das Ehepaar allerdings im Ehevertrag oder in einer Scheidungsfolgenvereinbarung auf den Versorgungsausgleich
            verzichten oder anstelle des Versorgungsausgleichs eine andere Leistung vereinbaren. Hierzu ist allerdings eine notarielle Beurkundung nötig.
          </p>
          <p>
            Es wird kein Versorgungsausgleich durchgeführt, wenn die Ehe weniger als drei Jahre andauerte und kein gesonderter Antrag auf Durchführung des
            Versorgungsausgleichs gestellt worden ist oder wenn der Ausgleichswert der jeweiligen Rentenansprüche zu gering ist.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Für welchen Zeitraum wird der Versorgungsausgleich durchgeführt?
          </Typography>
          <p>
            Es werden immer nur die Ansprüche aus der Ehezeit ausgeglichen. Doch wie wird die Dauer der Ehe bestimmt? Genau genommen dauert eine Ehe vom Tag der
            standesamtlichen Heirat bis zur rechtskräftigen Scheidung an. Für den Versorgungsausgleich wird die Ehezeit jedoch anders definiert. Laut Gesetz
            beginnt die Ehezeit am ersten Tag des Monats der Eheschließung und endet am letzten Tag des Monats vor der Zustellung des Scheidungsantrags bei
            Gericht.
          </p>
          <p>
            Beispiel für die Berechnung der Dauer:
            <br />
            Heirat am 10. April 2014
            <br />
            Zustellung des Antrags beim Gericht am 05. September 2018
          </p>
          <p>
            Die Ehezeit läuft in diesem Beispiel vom 01. April 2014 bis zum 31. August 2018. Für diesen Zeitraum wird dementsprechend der Versorgungsausgleich
            durchgeführt.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Der Ablauf der Versorgungsausgleichs
          </Typography>
          <MuiListNativeStyled
            listItems={[
              "Geht beim Familiengericht der Scheidungsantrag eines Ehepartners ein, übersendet das Gericht diesen Antrag zunächst dem anderen Ehepartner.",
              "Das Familiengericht stellt die Ehezeit für den Versorgungsausgleich fest und bittet beide Ehepartner, in einem Formular ihre in der Ehezeit erworbenen Versorgungsanrechte und die jeweils zuständigen Versorgungsträger anzugeben.",
              "Nach Eingang des ausgefüllten Formulars bittet das Familiengericht die Versorgungsträger um Auskunft über den Wert der einzelnen Anrechte. Die Versorgungsträger teilen dem Familiengericht die Höhe des am Ende der Ehezeit erreichten Versorgungsanrechts mit und machen einen Vorschlag zur Höhe des auszugleichenden Anteils.",
              "Das Familiengericht übersendet die erteilten Auskünfte der Versorgungsträger beiden Ehepartnern zur Kenntnis und zur Prüfung auf Vollständigkeit und Richtigkeit.",
              "Wurden keine Einwendungen gegen die Auskünfte erhoben, entscheidet das Familiengericht über den Versorgungsausgleich.",
              "Das Familiengericht übersendet den Ehepartnern und Versorgungsträgern eine Abschrift des Beschlusses. Innerhalb eines Monats nach Bekanntgabe können die Beteiligten Beschwerde dagegen einlegen.",
            ]}
          />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Welche Rentenansprüche muss ich angeben?
          </Typography>
          <p>
            Damit das Gericht einen Versorgungsausgleich durchführen kann, müssen alle Rentenansprüche von Ihnen und Ihrem Partner ab dem 18. Lebensjahr geklärt
            sein. Es werden auch alle Ansprüche, die Sie während der Schulausbildung, der Mutterschutzzeit oder einer Arbeitslosigkeit erworben haben,
            berücksichtigt. Folgende Ansprüche werden vom Gericht ausgeglichen:
          </p>
          <MuiListNativeStyled
            listItems={[
              "Gesetzliche Rentenversicherung (Deutsche Rentenversicherung Bund = BfA = Bundesanstalt für Angestellte; Deutsche Rentenversicherung eines Bundeslandes; Deutsche Rentenversicherung Knappschaft-Bahn-See)",
              "Beamtenversorgung (Pensionsansprüche)",
              "Ansprüche als Berufssoldat",
              "Betriebliche Altersversorgung",
              "Tariflich vereinbarte Zusatzversorgungen",
              "Zusatzversorgung des öffentlichen Dienstes (RZVK; KZVK)",
              "Berufsständische Altersversorgung (Freiberufler)",
              "Private Rentenversicherungen (z.B. Riesterrente; Rürup-Rente)",
              "Private Kapitallebensversicherung, sofern Sie bereits ein Rentenwahlrecht ausgeübt haben",
              "Rentengleiche, wiederkehrende Leistungen (z.B. Altershilfe für Landwirte)",
            ]}
          />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Kann ich den Versorgungsausgleich irgendwie beschleunigen?
          </Typography>
          <p>
            Der Versorgungsausgleich kann, wenn der Rentenverlauf von Ihnen und Ihrem Partner nicht vollständig geklärt ist, sehr lange dauern. Zwischen drei
            und sechs Monate benötigt das Gericht, um alle notwendigen Informationen einzuholen. Die gute Nachricht: Sie können dafür sorgen, dass der
            Versorgungsausgleich deutlich schneller geht.
          </p>
          <p>
            Klären Sie so früh wie möglich, das heißt bestenfalls schon während des Trennungsjahrs, ob das Rentenkonto und somit alle Informationen zu Ihren
            Rentenansprüchen und denen Ihres Partners aktuell sind. Oft fehlen Angaben aus Schulausbildung, Studium, Mutterschutzzeit oder Arbeitslosigkeit in
            Ihrem Rentenkonto. Dann müssen diese Zeiten nachträglich noch erfasst werden. Dieses Einholen von fehlenden Angaben nimmt erhebliche Zeit in
            Anspruch, da der Versorgungsträger Kontakt mit Ihnen aufnehmen und Sie zur Klärung der noch nicht berücksichtigten Zeiten auffordern muss.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Rentenansprüche so früh wie möglich klären
          </Typography>
          <p>
            Um ungeklärte Zeiträume und damit deutliche Verzögerungen zu vermeiden, sollten Sie und Ihr Partner so schnell wie möglich beim zuständigen Träger
            einen Antrag auf Kontenklärung stellen. Wir erklären Ihnen, wie Sie Ihre{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/scheidungsdauer/#rentenansprueche-klaeren"}>
              Rentenansprüche so früh wie möglich klären
            </MuiLink>
            .
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Wie kann ich den Versorgungsausgleich ausschließen?
          </Typography>
          <p>
            Durch den Ausschluss des Versorgungsausgleichs verringert sich die Dauer einer Scheidung um mehrere Monate. Der Ausschluss funktioniert wie folgt:
          </p>
          <MuiListNativeStyled
            listItems={[
              "Sie und Ihr Ehepartner müssen sich über den Ausschluss einig sein.",
              "Wenn Sie sich über den Ausschluss einig sind, machen Sie einen gemeinsamen Termin bei einem Notar Ihrer Wahl und lassen ein schriftliches Dokument aussetzen und beglaubigen, in dem Sie beide auf den Versorgungsausgleich verzichten.",
              "Das notariell beglaubigte Dokument lassen Sie uns zukommen, da es von uns direkt mit dem Scheidungsantrag bei Gericht eingereicht werden muss.",
            ]}
          />
          <p>
            Sollten Sie weitergehende Fragen zum Versorgungsausgleich oder zum Ausschluss des Versorgungsausgleichs haben, melden Sie sich gerne und wir helfen
            Ihnen weiter!
          </p>
          <DoubleButton buttonText={"zum Scheidungsantrag"} buttonLink={"/servicewelt"} backText={"Zurück zum Ratgeber"} backLink={"/ratgeber/familienrecht"} />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookDivorceChildren;
